/**
 * FSD Part 5 — Sales (docs §19161). C17: one section per FSD feature
 * specification — 5.2.1 (SLS-F01) through 5.2.8 (SLS-F08), section titles
 * from `nav.fsd.*` — then the
 * Part 5 requirement catalogue (5.1), whose items carry the requirement id
 * they answer as `code`.
 *
 * Item ids are stable and must stay so: favourites and recents in
 * localStorage key off them. Sections moved; items did not change.
 */
import type { NavGroup } from '../types'
import { PRICING_PERMISSIONS } from '@/modules/pricing/permissions'
import { DISCOUNT_PERMISSIONS } from '@/modules/sales/discounts/permissions'
import { SALES_PERMISSIONS } from '@/modules/sales/permissions'

export const P05_SALES_GROUPS: NavGroup[] = [
  {
    id: 's-5.2.1',
    kind: 'feature',
    fsd: '5.2.1',
    code: 'SLS-F01',
    labelKey: 'nav.fsd.s5_2_1.short',
    fullTitleKey: 'nav.fsd.s5_2_1.full',
    items: [
      {
        id: 'sales-quotes',
        labelKey: 'sales.routeTitles.quotes',
        to: '/sales/quotes',
        icon: 'mail',
        permission: SALES_PERMISSIONS.quoteRead,
        descriptionKey: 'nav.items.salesQuotes.description',
      },
      {
        id: 'sales-pipeline',
        labelKey: 'sales.routeTitles.pipeline',
        to: '/sales/reports/pipeline',
        icon: 'dashboard',
        permission: SALES_PERMISSIONS.reportRead,
      },
    ],
  },
  {
    id: 's-5.2.2',
    kind: 'feature',
    fsd: '5.2.2',
    code: 'SLS-F02',
    labelKey: 'nav.fsd.s5_2_2.short',
    fullTitleKey: 'nav.fsd.s5_2_2.full',
    items: [
      {
        id: 'sales-orders',
        labelKey: 'sales.routeTitles.orders',
        to: '/sales/orders',
        icon: 'inbox',
        permission: SALES_PERMISSIONS.orderRead,
        descriptionKey: 'nav.items.salesOrders.description',
      },
    ],
  },
  {
    id: 's-5.2.3',
    kind: 'feature',
    fsd: '5.2.3',
    code: 'SLS-F03',
    labelKey: 'nav.fsd.s5_2_3.short',
    fullTitleKey: 'nav.fsd.s5_2_3.full',
    items: [
      {
        // C2 (design §5.0, §5.7): the `internal/pricing` engine's price lists. P45 PS2 gave every
        // other pricing screen its own item below (the in-page tab strip, PricingNav, is gone).
        id: 'pricing-price-lists',
        labelKey: 'pricing.routeTitles.priceLists',
        to: '/pricing/price-lists',
        icon: 'settings',
        permission: PRICING_PERMISSIONS.priceRead,
        descriptionKey: 'nav.items.pricingPriceLists.description',
      },
      {
        id: 'pricing-promotions',
        labelKey: 'pricing.routeTitles.promotions',
        to: '/pricing/promotions',
        icon: 'inbox',
        permission: PRICING_PERMISSIONS.priceRead,
      },
      {
        id: 'pricing-precedence',
        labelKey: 'pricing.routeTitles.precedence',
        to: '/pricing/precedence',
        icon: 'history',
        permission: PRICING_PERMISSIONS.precedenceRead,
      },
      {
        id: 'pricing-simulator',
        labelKey: 'pricing.u62.simulator.title',
        to: '/pricing/simulate',
        icon: 'dashboard',
        permission: PRICING_PERMISSIONS.priceRead,
      },
      {
        id: 'pricing-explanations',
        labelKey: 'pricing.routeTitles.explanations',
        to: '/pricing/explanations',
        icon: 'history',
        permission: PRICING_PERMISSIONS.explanationRead,
      },
      {
        id: 'pricing-price-sets',
        labelKey: 'pricing.routeTitles.priceSets',
        to: '/pricing/price-sets',
        icon: 'download',
        permission: PRICING_PERMISSIONS.priceSetRead,
      },
      {
        id: 'pricing-item-settings',
        labelKey: 'pricing.routeTitles.itemSettings',
        to: '/pricing/item-settings',
        icon: 'settings',
        permission: PRICING_PERMISSIONS.priceRead,
      },
      {
        id: 'sales-trade-agreements',
        labelKey: 'sales.routeTitles.tradeAgreements',
        to: '/sales/trade-agreements',
        icon: 'inbox',
        permission: SALES_PERMISSIONS.agreementRead,
      },
    ],
  },
  {
    id: 's-5.2.4',
    kind: 'feature',
    fsd: '5.2.4',
    code: 'SLS-F04',
    labelKey: 'nav.fsd.s5_2_4.short',
    fullTitleKey: 'nav.fsd.s5_2_4.full',
    items: [
      {
        // Reads on `sales.settings.manage` because the screen is the editor.
        id: 'sales-settings',
        labelKey: 'sales.routeTitles.settings',
        to: '/sales/settings',
        icon: 'settings',
        permission: SALES_PERMISSIONS.settingsManage,
      },
      {
        // P45 PS3: the discount policies became the authority matrix; the id is kept so a
        // favourite or recent survives, the old path redirects (router/legacyRedirects.ts).
        id: 'sales-discount-policies',
        labelKey: 'sales.discounts.routeTitles.authorityMatrix',
        to: '/sales/authority-matrix',
        icon: 'shield',
        permission: DISCOUNT_PERMISSIONS.read,
      },
      {
        id: 'sales-discount-types',
        labelKey: 'sales.discounts.routeTitles.discountTypes',
        to: '/sales/discount-types',
        icon: 'inbox',
        permission: DISCOUNT_PERMISSIONS.read,
      },
      {
        id: 'sales-margin-floors',
        labelKey: 'sales.discounts.routeTitles.marginFloors',
        to: '/sales/margin-floors',
        icon: 'shield',
        permission: DISCOUNT_PERMISSIONS.marginRead,
      },
      {
        // P45 PS2: the one item-category master (R2). Procurement (requisition lines, supplier
        // qualification, tolerances, spend), sales (authority, floors) and QMS all key on it.
        id: 'pricing-categories',
        labelKey: 'pricing.routeTitles.categories',
        to: '/pricing/categories',
        icon: 'settings',
        permission: PRICING_PERMISSIONS.priceRead,
        related: ['4.2.1', '4.2.2'],
      },
    ],
  },
  {
    id: 's-5.2.5',
    kind: 'feature',
    fsd: '5.2.5',
    code: 'SLS-F05',
    labelKey: 'nav.fsd.s5_2_5.short',
    fullTitleKey: 'nav.fsd.s5_2_5.full',
    items: [
      {
        id: 'sales-shipments',
        labelKey: 'sales.routeTitles.shipments',
        to: '/sales/shipments',
        icon: 'download',
        permission: SALES_PERMISSIONS.shipmentRead,
        descriptionKey: 'nav.items.salesShipments.description',
      },
      /* What a partial shipment leaves behind (§8.11). Next to Shipments because
       * that is what creates it, and on the same `sales.shipment.read` its route
       * declares. */
      {
        id: 'sales-backorders',
        labelKey: 'sales.routeTitles.backorders',
        to: '/sales/backorders',
        icon: 'clock',
        permission: SALES_PERMISSIONS.shipmentRead,
      },
    ],
  },
  {
    id: 's-5.2.6',
    kind: 'feature',
    fsd: '5.2.6',
    code: 'SLS-F06',
    labelKey: 'nav.fsd.s5_2_6.short',
    fullTitleKey: 'nav.fsd.s5_2_6.full',
    items: [
      {
        id: 'sales-returns',
        labelKey: 'sales.routeTitles.returns',
        to: '/sales/returns',
        icon: 'refresh',
        permission: SALES_PERMISSIONS.returnRead,
        descriptionKey: 'nav.items.salesReturns.description',
      },
    ],
  },
  {
    id: 's-5.2.7',
    kind: 'feature',
    fsd: '5.2.7',
    code: 'SLS-F07',
    labelKey: 'nav.fsd.s5_2_7.short',
    fullTitleKey: 'nav.fsd.s5_2_7.full',
    items: [
      /* Deferred revenue is a schedule that posts, not a report that reads — it
       * has its own `sales.revrec.read`, held by the accountant who runs the
       * recognition rather than by the sales manager who reads the pipeline. */
      {
        id: 'sales-revrec',
        labelKey: 'sales.routeTitles.revRec',
        to: '/sales/revrec',
        icon: 'calendar',
        permission: SALES_PERMISSIONS.revRecRead,
      },
      {
        id: 'sales-obligations',
        labelKey: 'sales.routeTitles.obligations',
        to: '/sales/obligations',
        icon: 'calendar',
        permission: SALES_PERMISSIONS.obligationRead,
      },
    ],
  },
  {
    id: 's-5.2.8',
    kind: 'feature',
    fsd: '5.2.8',
    code: 'SLS-F08',
    labelKey: 'nav.fsd.s5_2_8.short',
    fullTitleKey: 'nav.fsd.s5_2_8.full',
    items: [
      {
        id: 'sales-subscriptions',
        labelKey: 'sales.routeTitles.subscriptions',
        to: '/sales/subscriptions',
        icon: 'clock',
        permission: SALES_PERMISSIONS.subscriptionRead,
        descriptionKey: 'nav.items.salesSubscriptions.description',
      },
      /* The plans a subscription is sold against — the catalogue behind the list
       * above it, sharing its `sales.subscription.read`. */
      {
        id: 'sales-subscription-plans',
        labelKey: 'sales.routeTitles.subscriptionPlans',
        to: '/sales/subscription-plans',
        icon: 'settings',
        permission: SALES_PERMISSIONS.subscriptionRead,
      },
      {
        id: 'sales-mrr',
        labelKey: 'sales.routeTitles.mrr',
        to: '/sales/reports/mrr',
        icon: 'dashboard',
        permission: SALES_PERMISSIONS.reportRead,
        descriptionKey: 'nav.items.salesMrr.description',
      },
    ],
  },
  // Part 5 requirement catalogue (5.1). Screens answering a catalogue
  // requirement that no feature specification covers; `code` names it.
  {
    id: 's-5.1',
    kind: 'catalogue',
    fsd: '5.1',
    labelKey: 'nav.fsd.s5_1.short',
    fullTitleKey: 'nav.fsd.s5_1.full',
    items: [
      {
        id: 'sales-by',
        labelKey: 'sales.routeTitles.salesBy',
        to: '/sales/reports/sales-by',
        icon: 'scale',
        permission: SALES_PERMISSIONS.reportRead,
        code: 'SLS-FR-015',
      },
      {
        id: 'sales-customer-contracts',
        labelKey: 'sales.routeTitles.customerContracts',
        to: '/sales/customer-contracts',
        icon: 'inbox',
        permission: SALES_PERMISSIONS.contractRead,
        code: 'SLS-FR-017',
      },
      {
        id: 'sales-rebate-programs',
        labelKey: 'sales.routeTitles.rebatePrograms',
        to: '/sales/rebate-programs',
        icon: 'scale',
        permission: SALES_PERMISSIONS.rebateRead,
        code: 'SLS-FR-019',
      },
      {
        id: 'sales-rebate-accruals',
        labelKey: 'sales.routeTitles.rebateAccruals',
        to: '/sales/rebate-accruals',
        icon: 'scale',
        permission: SALES_PERMISSIONS.rebateRead,
        code: 'SLS-FR-019',
      },
    ],
  },
]
